/**
 * Attention, as the matching record measures it (2026-10-27). One definition
 * for the matching page's cards (src/Platform/hooks/useSearchViewSeen.ts) and
 * the profile page (src/Talent/components/profile/useProfileStudy.ts).
 *
 * An attended second is a second of wall time during which ALL of these hold:
 *   - the thing is on screen (a card: at least half of it, or half the screen
 *     for a tall one; the caller decides and calls `setOnScreen`);
 *   - the tab is visible;
 *   - no dialog covers the page;
 *   - the specialist was active: a pointer, scroll, wheel, key or touch event
 *     within the last ACTIVE_WINDOW_MS. Arriving (the clock starting, or the
 *     tab becoming visible again) counts as activity.
 * Unbroken runs of such seconds are stretches. A stretch ends when any
 * condition stops; one that ends by going idle ends at the last activity plus
 * ACTIVE_WINDOW_MS. A stretch shorter than MIN_STRETCH_MS is dropped (a
 * scroll-past is not attention). Per key the clock keeps:
 *   - attendedMs: the sum of stretches, each capped at STRETCH_CAP_MS;
 *   - rawMs: the same sum without the cap;
 *   - longestMs: the longest single stretch;
 *   - stretches: how many; capped: whether the cap cut one;
 *   - firstAt: when the first counted stretch began.
 * Totals only ever grow, so they are sent as running totals and the server
 * keeps the larger value: a retried or late report never counts twice.
 *
 * No timers: going idle is worked out when the clock is next read or touched,
 * from the time of the last activity. `now` is injectable for tests.
 */

export const ACTIVE_WINDOW_MS = 30_000;
export const MIN_STRETCH_MS = 1_000;
export const STRETCH_CAP_MS = 120_000;

export interface AttentionTotals {
  attendedMs: number;
  rawMs: number;
  longestMs: number;
  stretches: number;
  capped: boolean;
  firstAt: number | null;
}

/** The whole seconds a report carries (the server's integer columns). */
export interface AttentionSeconds {
  attendedS: number;
  attendedRawS: number;
  longestStretchS: number;
  stretches: number;
  capped: boolean;
}

export const emptyTotals = (): AttentionTotals => ({ attendedMs: 0, rawMs: 0, longestMs: 0, stretches: 0, capped: false, firstAt: null });

export function toSeconds(t: AttentionTotals): AttentionSeconds {
  return {
    attendedS: Math.floor(t.attendedMs / 1000),
    attendedRawS: Math.floor(t.rawMs / 1000),
    longestStretchS: Math.floor(t.longestMs / 1000),
    stretches: t.stretches,
    capped: t.capped,
  };
}

/** Totals restored from storage: anything malformed is ignored. */
export function isTotals(v: any): v is AttentionTotals {
  return !!v && ['attendedMs', 'rawMs', 'longestMs', 'stretches'].every((k) => Number.isFinite(v[k]) && v[k] >= 0)
    && typeof v.capped === 'boolean' && (v.firstAt === null || Number.isFinite(v.firstAt));
}

function addStretch(t: AttentionTotals, start: number, ms: number): AttentionTotals {
  if (ms < MIN_STRETCH_MS) return t;
  return {
    attendedMs: t.attendedMs + Math.min(ms, STRETCH_CAP_MS),
    rawMs: t.rawMs + ms,
    longestMs: Math.max(t.longestMs, ms),
    stretches: t.stretches + 1,
    capped: t.capped || ms > STRETCH_CAP_MS,
    firstAt: t.firstAt ?? start,
  };
}

interface Entry {
  onScreen: boolean;
  /** When the open stretch began, or null. */
  start: number | null;
  totals: AttentionTotals;
}

export class AttentionClock {
  private entries = new Map<string, Entry>();
  private visible: boolean;
  private paused = false;
  private lastActivity: number;

  constructor(private now: () => number = () => Date.now(), opts: { visible?: boolean } = {}) {
    this.visible = opts.visible ?? true;
    this.lastActivity = this.now();
  }

  private entry(key: string): Entry {
    let e = this.entries.get(key);
    if (!e) {
      e = { onScreen: false, start: null, totals: emptyTotals() };
      this.entries.set(key, e);
    }
    return e;
  }

  private isActive(at: number): boolean {
    return at - this.lastActivity <= ACTIVE_WINDOW_MS;
  }

  /** Where an open stretch ends if it ended at `at`: no later than the end of the active window. */
  private endAt(e: Entry, at: number): number {
    return Math.max(e.start as number, Math.min(at, this.lastActivity + ACTIVE_WINDOW_MS));
  }

  private close(e: Entry, at: number): void {
    if (e.start === null) return;
    e.totals = addStretch(e.totals, e.start, this.endAt(e, at) - e.start);
    e.start = null;
  }

  private open(e: Entry, at: number): void {
    if (e.start === null && e.onScreen && this.visible && !this.paused && this.isActive(at)) e.start = at;
  }

  private closeAll(at: number): void {
    this.entries.forEach((e) => this.close(e, at));
  }

  private openAll(at: number): void {
    this.entries.forEach((e) => this.open(e, at));
  }

  /** A pointer, scroll, wheel, key or touch event. After an idle spell it starts new stretches. */
  activity(): void {
    const at = this.now();
    if (!this.isActive(at)) {
      this.closeAll(at); // they ended when the active window ran out
      this.lastActivity = at;
      this.openAll(at);
    } else {
      this.lastActivity = at;
    }
  }

  setOnScreen(key: string, onScreen: boolean): void {
    const at = this.now();
    const e = this.entry(key);
    if (e.onScreen === onScreen) return;
    e.onScreen = onScreen;
    if (onScreen) this.open(e, at);
    else this.close(e, at);
  }

  /** The tab hidden or shown. Coming back is the specialist arriving, so it counts as activity. */
  setVisible(visible: boolean): void {
    const at = this.now();
    if (visible === this.visible) return;
    if (!visible) {
      this.closeAll(at);
      this.visible = false;
      return;
    }
    this.visible = true;
    this.lastActivity = at;
    this.openAll(at);
  }

  /** A dialog over the page. */
  setPaused(paused: boolean): void {
    const at = this.now();
    if (paused === this.paused) return;
    if (paused) {
      this.closeAll(at);
      this.paused = true;
      return;
    }
    this.paused = false;
    this.openAll(at);
  }

  /** Close every stretch (the page going away). Keys stay on screen as far as the clock knows. */
  stopAll(): void {
    const at = this.now();
    this.entries.forEach((e) => {
      this.close(e, at);
      e.onScreen = false;
    });
  }

  /** Totals from before (a reload or a return in the same tab), added under this key. */
  restore(key: string, totals: AttentionTotals): void {
    const e = this.entry(key);
    const t = e.totals;
    e.totals = {
      attendedMs: t.attendedMs + totals.attendedMs,
      rawMs: t.rawMs + totals.rawMs,
      longestMs: Math.max(t.longestMs, totals.longestMs),
      stretches: t.stretches + totals.stretches,
      capped: t.capped || totals.capped,
      firstAt: totals.firstAt === null ? t.firstAt : t.firstAt === null ? totals.firstAt : Math.min(t.firstAt, totals.firstAt),
    };
  }

  /** The totals so far, counting an open stretch as if it ended now. */
  totals(key: string): AttentionTotals {
    const e = this.entries.get(key);
    if (!e) return emptyTotals();
    if (e.start === null) return e.totals;
    return addStretch(e.totals, e.start, this.endAt(e, this.now()) - e.start);
  }

  keys(): string[] {
    return [...this.entries.keys()];
  }
}

/** On screen enough to have been looked at: half the element, or half the screen for a tall one. */
export function isOnScreen(
  entry: Pick<IntersectionObserverEntry, 'isIntersecting' | 'intersectionRatio' | 'intersectionRect' | 'rootBounds'>,
): boolean {
  if (!entry.isIntersecting) return false;
  if (entry.intersectionRatio >= 0.5) return true;
  const screen = entry.rootBounds?.height || window.innerHeight;
  return screen > 0 && entry.intersectionRect.height >= screen / 2;
}

/** The events that count as the specialist being active. */
export const ACTIVITY_EVENTS = ['pointermove', 'pointerdown', 'wheel', 'scroll', 'keydown', 'touchstart'] as const;

/** Listen for activity on the window (capture, passive: never blocks a scroll). Returns the cleanup. */
export function listenForActivity(onActivity: () => void): () => void {
  const opts: AddEventListenerOptions = { capture: true, passive: true };
  ACTIVITY_EVENTS.forEach((type) => window.addEventListener(type, onActivity, opts));
  return () => ACTIVITY_EVENTS.forEach((type) => window.removeEventListener(type, onActivity, opts));
}
